import { surfaceField } from '../compile/from-base-encounter-media';
import { readInventoryField } from '../compile/inventory-raw-readers';
import type { AppliedFrame, AppliedRow } from './applied-frame';
import { encounterField, encounterCreatureField } from '../../shared/array-types';
import { applyExplorationStateRows } from '../compile/from-base-encounter-exploration';
import { fromBaseRecord } from '../compile/from-base-context';
import { applyWaterLevelReadback } from '../movement/water-level-plan-21';

export interface SceneSaveDelta {
    lane: string;
    data: AppliedRow;
    openingId?: string;
    roomId?: string;
    viewerId?: string;
    operation?: string;
    baseRevision?: string;
    shapeRevision?: string;
}

/** Saved metadata changes one room/binding. Geometry, rosters and catalog stay resident. */
export function sceneSaveShape(shape: AppliedRow, delta: SceneSaveDelta): AppliedRow {
    const { lane, data } = delta;
    if (lane === 'room_music' || lane === 'room_ambience') {
        const boxes = shape.boxes as AppliedRow[];
        const index = boxes.findIndex(box => box.id === delta.roomId);
        if (index < 0) return shape;
        const box = boxes[index]!, creation = fromBaseRecord(box.creation) ? box.creation : {};
        const fields = lane === 'room_music' ? { music: data.music } : { ambience: data.ambience, ...(data.climate ? { climate: data.climate } : {}) };
        const next = boxes.slice(); next[index] = { ...box, creation: { ...creation, ...fields } };
        return { ...shape, boxes: next };
    }
    if (lane === 'curtain' && data.bindings) {
        const bindings = data.bindings as AppliedRow;
        return { ...shape, openings: (shape.openings as AppliedRow[]).map(opening => {
            if (!Object.prototype.hasOwnProperty.call(bindings, String(opening.id))) return opening;
            const creation = { ...fromBaseRecord(opening.creation) ? opening.creation : {} };
            if (bindings[String(opening.id)]) { creation.covering = bindings[String(opening.id)]; delete creation.curtainSuppressed; }
            else { delete creation.covering; creation.curtainSuppressed = true; }
            return { ...opening, creation };
        }) };
    }
    if (lane === 'roof') {
        const authoring = fromBaseRecord(shape.authoring) ? shape.authoring : {};
        const roofs = fromBaseRecord(authoring.buildingRoofs) ? authoring.buildingRoofs : {};
        const changes = data.roofs as AppliedRow, rooms = data.rooms as AppliedRow;
        return { ...shape, authoring: { ...authoring, buildingRoofs: { ...roofs, ...changes } },
            boxes: (shape.boxes as AppliedRow[]).map(box => Object.prototype.hasOwnProperty.call(rooms, String(box.id))
                ? { ...box, creation: { ...fromBaseRecord(box.creation) ? box.creation : {}, ...rooms[String(box.id)] as AppliedRow } } : box) };
    }
    return shape;
}

/** Apply semantic saved sections through their existing lean-row readers. No encounter compilation. */
export function applySceneSaveState(source: unknown, frame: AppliedFrame, raw: boolean): unknown {
    const delta = frame.inputs?.scene_save as SceneSaveDelta | undefined;
    if (!delta || !fromBaseRecord(source) || String(encounterField(source, 'encounter_id') ?? '').toUpperCase() !== frame.encounter_id.toUpperCase()) return source;
    const next = { ...source }; // dnr-clone: allow size=one changed encounter shell; why=preserve every held member and inventory while installing saved metadata
    const { lane, data } = delta;
    if (['room_music', 'room_ambience', 'curtain', 'roof'].includes(lane)) {
        const saved = encounterField(source, 'encounter_shape');
        if (saved && fromBaseRecord(source.encounters)) {
            const shape = (typeof saved === 'string' ? JSON.parse(saved) : saved) as AppliedRow;
            const next = sceneSaveShape(shape, delta);
            if (next !== shape) {
                const patched = { ...source.encounters, encounter_shape: JSON.stringify(next) };
                return { ...source, encounters: patched };
            }
        }
    }
    if (raw && lane === 'curtain' && Array.isArray(data.closureRows) && Array.isArray(source.inventory)) {
        const saved = data.closureRows as AppliedRow[];
        const patch = (nodes: AppliedRow[]): AppliedRow[] => {
            let changed = false;
            const result = nodes.map(node => {
                const id = String(readInventoryField(node, 'inventory_id') ?? '').toUpperCase();
                const state = saved.find(row => String(readInventoryField(row, 'inventory_closure_state_closure_id') ?? '').toUpperCase() === id);
                if (state) { changed = true; return { ...node, inventory_closure_states: [state] }; }
                const children = Array.isArray(node.inventory_in_container) ? patch(node.inventory_in_container as AppliedRow[]) : node.inventory_in_container;
                if (children !== node.inventory_in_container) { changed = true; return { ...node, inventory_in_container: children }; }
                return node;
            });
            return changed ? result : nodes;
        };
        next.inventory = patch(source.inventory as AppliedRow[]);
    }
    if (lane.startsWith('exploration_')) {
        const rows = data.states as AppliedRow[];
        if (raw) {
            const held = Array.isArray(source.encounter_exploration_states) ? (source.encounter_exploration_states as AppliedRow[]).slice() : [];
            for (const row of rows) {
                const index = held.findIndex(old => surfaceField(old, 'encounter_exploration_state_id') === surfaceField(row, 'encounter_exploration_state_id'));
                if (index < 0) held.push(row); else if (Number(surfaceField(held[index]!, 'encounter_exploration_state_revision') ?? 0) < Number(surfaceField(row, 'encounter_exploration_state_revision'))) held[index] = row;
            }
            next.encounter_exploration_states = held;
        } else {
            next.encounter_exploration_states_arr = fromBaseRecord(source.encounter_exploration_states_arr) ? { ...source.encounter_exploration_states_arr } : {};
            applyExplorationStateRows(next, rows);
        }
    }
    if (lane === 'water_level') {
        if (fromBaseRecord(source.encounters)) next.encounters = { ...source.encounters };
        const affected = new Set((data.wetRows as Array<{ actor_id: string }>).map(row => row.actor_id.toUpperCase()));
        if (!raw && affected.size && Array.isArray(source.encounter_creatures_arr)) next.encounter_creatures_arr = (source.encounter_creatures_arr as AppliedRow[]).map(row =>
            affected.has(String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') ?? '').toUpperCase())
                ? { ...row, statuses: { ...fromBaseRecord(row.statuses) ? row.statuses : {} } } : row);
        applyWaterLevelReadback(next, data.levelFt as number | null, raw ? [] : data.wetRows as Array<{ actor_id: string; after: AppliedRow }>);
    }
    return next;
}
